'use client';

import { useAuth } from '@morfi/backend-sdk-v2/client';

import { useLayoutStore } from '@/stores/layout';

export interface AuthGate {
  /** True when a user is signed in. */
  isAuthenticated: boolean;
  /** True while the SDK is restoring the authenticated user session. */
  isLoading: boolean;
  /**
   * Imperatively open the login modal. Use when a child has already decided
   * auth is required (e.g. a vote pill that owns its own `isAuthenticated`
   * prop and just needs the parent to surface the gate).
   */
  openLogin: () => void;
  /**
   * Returns `true` when the viewer is authenticated. When not, opens the
   * login modal and returns `false`. Use as an early-return guard inside a
   * handler that does other pre-work first (e.g. `preventDefault`):
   *
   * ```ts
   * function onReact(next, e) {
   *   e.preventDefault();
   *   e.stopPropagation();
   *   if (!ensureAuthenticated()) return;
   *   mutation.mutate(...);
   * }
   * ```
   */
  ensureAuthenticated: () => boolean;
  /**
   * Wraps a handler so it only runs when authenticated; otherwise opens the
   * login modal. Use for simple click handlers with no other pre-work:
   *
   * ```tsx
   * <AnimatedButton onClick={withAuth(() => setBoostOpen(true))} />
   * ```
   */
  // ALLOW-UNKNOWN: variadic-handler generic bound — the idiomatic args-tuple constraint for a higher-order wrapper.
  withAuth: <Args extends readonly unknown[]>(
    handler: (...args: Args) => void,
  ) => (...args: Args) => void;
}

/**
 * The single source of truth for gating logged-out users out of an action.
 *
 * Every "you must be signed in to do this" control routes through here so the
 * gate behaves identically everywhere: it flips `connectAuthOpen` on the
 * layout store, which the `AuthModalBridge` in `app-shell.tsx` turns into the
 * Turnkey login modal (capturing the return-to target first).
 *
 * Pick the member that matches the call shape — `ensureAuthenticated()` for an
 * early-return guard, `withAuth()` to wrap a bare click handler, `openLogin()`
 * when a child already knows auth is needed, and `isAuthenticated` to render
 * conditionally.
 */
export function useAuthGate(): AuthGate {
  const { isAuthenticated, isLoading } = useAuth();
  const setConnectAuthOpen = useLayoutStore((s) => s.setConnectAuthOpen);

  function openLogin(): void {
    setConnectAuthOpen(true);
  }

  function ensureAuthenticated(): boolean {
    if (!isAuthenticated) {
      setConnectAuthOpen(true);
      return false;
    }
    return true;
  }

  // ALLOW-UNKNOWN: variadic-handler generic bound — the idiomatic args-tuple constraint for a higher-order wrapper.
  function withAuth<Args extends readonly unknown[]>(
    handler: (...args: Args) => void,
  ): (...args: Args) => void {
    return (...args: Args): void => {
      if (!ensureAuthenticated()) return;
      handler(...args);
    };
  }

  return { isAuthenticated, isLoading, openLogin, ensureAuthenticated, withAuth };
}
